import React from 'react';

import { FormattedMessage } from 'react-intl';

import SimpleTag from '@atlaskit/tag/simple-tag';

import { shortHash } from 'src/components/commit-list/src/utils/string-helpers';
import GenericMessage from 'src/components/generic-message';
import { PullRequestCommit } from 'src/components/types';
import common from 'src/i18n/common';
import urls from 'src/urls';

import messages from './diff.i18n';

export const BrokenDiffMessage = (): JSX.Element => (
  <GenericMessage
    iconType="error"
    title={<FormattedMessage {...common.errorHeading} />}
  >
    <FormattedMessage
      tagName="p"
      {...messages.diffError}
      values={{
        contactLink: (
          <a href="https://support.atlassian.com/">support.atlassian.com</a>
        ),
      }}
    />
  </GenericMessage>
);

export const MissingDiffMessage = (): JSX.Element => (
  <GenericMessage
    iconType="warning"
    title={<FormattedMessage {...messages.missingDiffsTitle} />}
  >
    <FormattedMessage {...messages.missingDiffs} />
  </GenericMessage>
);

export const MissingDiffOnMergedPrMessage = (
  mergeCommit: PullRequestCommit
): JSX.Element => {
  const mergeCommitHash = shortHash(mergeCommit.hash);
  const mergeCommitLink = mergeCommit.links.html.href;
  return (
    <GenericMessage
      iconType="warning"
      title={<FormattedMessage {...messages.missingDiffsErrorTitle} />}
    >
      <FormattedMessage
        tagName="p"
        {...messages.missingDiffsOnMergedPr}
        values={{
          newLine: <br />,
          mergeCommitShortHash: <a href={mergeCommitLink}>{mergeCommitHash}</a>,
        }}
      />
    </GenericMessage>
  );
};

export const UnhandledMergeConflictsMessage = (): JSX.Element => (
  <GenericMessage
    iconType="error"
    title={<FormattedMessage {...messages.unhandledMergeConflictsTitle} />}
  >
    <FormattedMessage
      tagName="p"
      {...messages.unhandledMergeConflictsError}
      values={{
        resolveMergeConflictsLink: (
          <a
            href={urls.external.resolveMergeConflictDocs}
            rel="noopener"
            target="_blank"
          >
            <FormattedMessage {...messages.learnHowLink} />
          </a>
        ),
      }}
    />
  </GenericMessage>
);

export const DiffTimeoutMessage = (): JSX.Element => (
  <GenericMessage
    iconType="error"
    title={<FormattedMessage {...common.errorHeading} />}
  >
    <FormattedMessage
      tagName="p"
      {...messages.diffTimeout}
      values={{
        docLink: (
          <a href="https://support.atlassian.com/bitbucket-cloud/docs/limits-for-viewing-content-and-diffs/">
            <FormattedMessage {...messages.diffLimitsDocTitle} />
          </a>
        ),
        supportLink: (
          <a href="https://support.atlassian.com/">support.atlassian.com</a>
        ),
      }}
    />
  </GenericMessage>
);

export const NoCommonAncestorMessage = (): JSX.Element => (
  <GenericMessage
    iconType="error"
    title={<FormattedMessage {...messages.noCommonAncestorTitle} />}
  >
    <FormattedMessage tagName="p" {...messages.noCommonAncestor} />
  </GenericMessage>
);

export const AmbiguousRefsMessage = ({
  sourceHash,
  destinationHash,
}: {
  sourceHash: string;
  destinationHash: string;
}): JSX.Element => (
  <GenericMessage
    iconType="error"
    title={<FormattedMessage {...messages.missingDiffsErrorTitle} />}
  >
    <FormattedMessage
      tagName="p"
      {...messages.ambiguousRefs}
      values={{
        sourceHash: <SimpleTag text={sourceHash} />,
        destHash: <SimpleTag text={destinationHash} />,
      }}
    />
  </GenericMessage>
);
